import type { FormAnswers, WebForm, WebFormField } from '@church/nextjs-ui/website/forms';
import { isCampusField, plain, same } from '@church/nextjs-ui/website/forms';

/**
 * ANSWERS THE PAGE ALREADY KNOWS
 * ==============================
 * A form embedded on the Lake Orion page should not ask which campus, and a
 * form reached from the Men's Ministry page should arrive with Men's Ministry
 * already ticked. Everything here turns what the host page knows into the
 * same `FormAnswers` map a typed answer produces — never into a hidden value,
 * so the answer is on screen and anybody can change it.
 */

export interface PresetInput {
  /** `data-answers`: a JSON object of Form_Field_ID to answer. */
  answers?: string | null;
  /** `data-campus`: a campus name, matched against the campus question's options. */
  campus?: string | null;
  /** `data-interest`: labels (comma separated) ticked in any multiselect group. */
  interest?: string | null;
}

/** `data-answers` as written in the embed: `{"13218":"Baptism"}`. Bad JSON is ignored. */
export function parseAnswerAttr(raw: string | null | undefined): FormAnswers {
  if (!raw?.trim()) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: FormAnswers = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (/^\d{1,9}$/.test(key.trim()) && typeof value === 'string' && value.trim()) {
        out[key.trim()] = value.trim();
      }
    }
    return out;
  } catch {
    return {};
  }
}

/** "Farmington Hills Campus" -> "farmington-hills", the site's own campus slugs. */
function slug(name: string): string {
  return name
    .replace(/\s+campus$/i, '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * The campus a page is about, read off its own address: `/locations/lake-orion/`
 * and `/kids-ministry-lake-orion/` are both about Lake Orion. Longest slug
 * first, so `/locations/troy-espanol/` is Troy - Español and never Troy.
 *
 * This is what lets one shared WordPress section carry the same embed on
 * fourteen campus pages and still arrive with the right campus answered.
 */
export function campusFromPath(path: string, campusNames: string[]): string | null {
  const segments = path.toLowerCase().split('/').filter(Boolean);
  if (!segments.length) return null;
  const ranked = [...campusNames].sort((a, b) => slug(b).length - slug(a).length);
  for (const name of ranked) {
    const s = slug(name);
    if (!s) continue;
    if (segments.some((seg) => seg === s || seg.endsWith(`-${s}`))) return name;
  }
  return null;
}

/** The option of `field` that means `name`, or null when the form has no such option. */
export function matchOption(field: WebFormField, name: string): string | null {
  return field.options.find((o) => same(plain(o), name)) ?? null;
}

/** Every campus question on a form: the configured ones and the fourteen-campus dropdowns. */
export function campusFields(form: WebForm, campusNames: string[]): WebFormField[] {
  return form.fields.filter((f) => !f.hidden && isCampusField(f, campusNames));
}

/**
 * The checkbox children of the form's multiselect groups whose label names one
 * of `labels`. Ticking a child is how Woodside's multiselect stores an answer,
 * so a preset ticks children, never the parent.
 */
export function interestChildren(form: WebForm, labels: string[]): WebFormField[] {
  const wanted = labels.map((l) => l.trim()).filter(Boolean);
  if (!wanted.length) return [];
  return form.fields.filter(
    (f) =>
      !f.hidden &&
      f.parentId !== null &&
      f.kind === 'checkbox' &&
      wanted.some((w) => same(plain(f.title ?? f.label), w))
  );
}

/**
 * Everything the page knows, folded into one answer map. Order matters: the
 * explicit `data-answers` wins over the friendlier shorthands, because it is
 * the one that names field ids and cannot be ambiguous.
 */
export function presetAnswers(
  form: WebForm,
  campusNames: string[],
  input: PresetInput
): FormAnswers {
  const out: FormAnswers = {};

  if (input.campus?.trim()) {
    for (const field of campusFields(form, campusNames)) {
      const option = matchOption(field, input.campus.trim());
      if (option) out[String(field.id)] = option;
    }
  }

  if (input.interest?.trim()) {
    for (const child of interestChildren(form, input.interest.split(','))) {
      out[String(child.id)] = 'Yes';
    }
  }

  Object.assign(out, parseAnswerAttr(input.answers));
  return out;
}
